import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT, SERVICE_OPTIONS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  email: "",
  phone: "",
  property_type: "domestic",
  service: "",
  message: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in your name, email, phone and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Request received — we'll be in touch within one business day.");
      setForm(empty);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-black border-white/15 h-12 rounded-none text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-cyanbrand";

  return (
    <section id="quote" data-testid="quote" className="relative border-t border-white/10 py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          {/* Left copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-cyanbrand">
              / Get protected
            </span>
            <h2 className="mt-4 font-display text-4xl md:text-6xl font-medium tracking-tight">
              Book your free site survey.
            </h2>
            <p className="mt-6 max-w-md font-body text-base text-white/55 leading-relaxed">
              Tell us about your property and what you&apos;d like to protect. A
              certified engineer from {CONTACT.parent} will scope the job and send
              you a no-obligation quote.
            </p>

            <div className="mt-10 space-y-4 font-body text-sm">
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="quote-email-link"
                className="block text-white/70 hover:text-cyanbrand transition-colors"
              >
                {CONTACT.email}
              </a>
              <a
                href={CONTACT.phoneHref}
                data-testid="quote-phone-link"
                className="block text-white/70 hover:text-cyanbrand transition-colors"
              >
                {CONTACT.phone}
              </a>
              <p className="max-w-xs text-white/40">{CONTACT.address}</p>
              <p className="flex items-center gap-2 text-white/40">
                <ShieldCheck className="h-4 w-4 text-cyanbrand" /> {CONTACT.hours}
              </p>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.form
            onSubmit={submit}
            data-testid="quote-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="border border-white/10 bg-[#0a0a0a] p-6 md:p-10"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">
                  Full name
                </Label>
                <Input
                  data-testid="quote-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Jane Smith"
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">
                  Phone
                </Label>
                <Input
                  data-testid="quote-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="07123 456789"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">
                Email
              </Label>
              <Input
                data-testid="quote-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="jane@email.com"
                className={inputCls}
              />
            </div>

            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">
                  Property type
                </Label>
                <div className="flex gap-2">
                  {["domestic", "commercial"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`quote-type-${t}`}
                      onClick={() => setForm((f) => ({ ...f, property_type: t }))}
                      className={`h-12 flex-1 border text-sm capitalize transition-colors duration-200 ${
                        form.property_type === t
                          ? "border-cyanbrand bg-cyanbrand/10 text-cyanbrand"
                          : "border-white/15 text-white/60 hover:border-white/40"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">
                  Service
                </Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger
                    data-testid="quote-service"
                    className="h-12 rounded-none border-white/15 bg-black text-white focus:ring-0 focus:border-cyanbrand"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="border-white/15 bg-[#111]">
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-opt-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">
                Message <span className="text-white/25">(optional)</span>
              </Label>
              <Textarea
                data-testid="quote-message"
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us about your property, entry points, or concerns…"
                className="min-h-[110px] rounded-none border-white/15 bg-black text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-cyanbrand"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-btn"
              className="group mt-8 flex w-full items-center justify-center gap-2 bg-cyanbrand py-4 font-semibold text-black transition-transform duration-200 hover:scale-[1.01] disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Request my quote{" "}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
